
/**
 * JavaScript implementation of TextSearchMatcher
 * Replicates EF.Functions.WebSearchToTsQuery behavior for browser-based text search
 */

export class SearchMatch {
    constructor(searchTerm, matchedText, offset, length) {
        this.searchTerm = searchTerm;
        this.matchedText = matchedText;
        this.offset = offset;
        this.length = length;
    }
}

export class ParsedTerm {
    constructor(text, normalizedText = '', isPhrase = false, isNegated = false, words = null) {
        this.text = text;
        this.normalizedText = normalizedText;
        this.isPhrase = isPhrase;
        this.isNegated = isNegated;
        this.words = words;
    }
}

export class TextSearchMatcher {
    // Simple English stop words (similar to PostgreSQL's basic set)
    static stopWords = new Set([
        'a', 'an', 'and', 'are', 'as', 'at', 'be', 'been', 'by', 'for', 
        'from', 'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 
        'that', 'the', 'to', 'was', 'will', 'with', 'this', 
        'but', 'they', 'have', 'had', 'what', 'said', 'each', 'which', 
        'do', 'how', 'their', 'if', 'up', 'out', 'many', 'then', 'them'
    ]);

    // Basic stemming rules (simplified version of what PostgreSQL does)
    static stemRules = new Map([
        ['ies', 'y'], ['ied', 'y'], ['ying', 'y'], ['ing', ''], 
        ['ly', ''], ['ed', ''], ['s', '']
    ]);

    static stemExceptions = new Set([
        // Common function words
        'was', 'has', 'his', 'its', 'yes', 'this', 'thus', 'as', 'is', 'us',
        
        // Common nouns that end in 's' but aren't plural
        'news', 'bias', 'basis', 'crisis', 'thesis', 'analysis', 'oasis',
        'virus', 'bonus', 'minus', 'focus', 'status', 'campus', 'genus',
        'atlas', 'cosmos', 'chaos', 'lens', 'mass', 'loss', 'boss', 'mess',
        
        // Academic subjects ending in 's'
        'physics', 'mathematics', 'economics', 'politics', 'athletics',
        'linguistics', 'statistics', 'genetics', 'mechanics', 'electronics',
        
        // Diseases
        'diabetes', 'measles', 'mumps', 'rabies', 'rickets', 'shingles',
        
        // Games
        'billiards', 'chess', 'darts', 'cards',
        
        // Other common words
        'series', 'species', 'means', 'across', 'always', 'famous', 'various',
        'serious', 'obvious', 'curious', 'gloss', 'glass', 'brass', 'class',
        'grass', 'dress', 'stress', 'press', 'unless', 'business'
    ]);

    /**
     * Finds matches in plain text based on search term
     * @param {string} plainText - The text to search in
     * @param {string} searchTerm - The search term (supports websearch_to_tsquery syntax)
     * @returns {SearchMatch[]} Array of search matches with offsets
     */
    static findMatches(plainText, searchTerm) {
        const matches = [];

        // Parse the search term similar to websearch_to_tsquery
        const searchTerms = this.parseSearchTerm(searchTerm);

        for (const term of searchTerms) {
            if (term.isPhrase) {
                // Handle phrase searches (quoted text)
                this.findPhraseMatches(plainText, term, matches);
            } else if (!term.isNegated) {
                // Handle individual word searches (skip negated terms for highlighting)
                this.findWordMatches(plainText, term, matches);
            }
        }

        // Sort matches by offset and remove duplicates
        return matches
            .sort((a, b) => a.offset - b.offset)
            .filter((match, index, arr) => 
                index === 0 || 
                match.offset !== arr[index - 1].offset || 
                match.length !== arr[index - 1].length
            );
    }

    /**
     * Parses search term into individual components
     * @param {string} searchTerm - The search term to parse
     * @returns {ParsedTerm[]} Array of parsed terms
     */
    static parseSearchTerm(searchTerm) {
        const terms = [];
        // Match quoted phrases or individual words
        const regex = /"([^"]*)"|(\S+)/g;
        let match;

        while ((match = regex.exec(searchTerm)) !== null) {
            if (match[1] !== undefined) { // Quoted phrase
                const phrase = match[1].trim();
                if (phrase) {
                    const rawWords = phrase.split(/\s+/);
                    if (rawWords.length === 1) {
                        const rawWord = rawWords[0];
                        if (rawWord.length > 0 && !this.stopWords.has(rawWord.toLowerCase())) {
                            //do not consider negation in a quoted phrase
                            terms.push(new ParsedTerm(rawWord, this.normalizeWord(rawWord), false, false));
                        }
                    } else if (rawWords.length > 1) {
                        //don't remove stop words from a quoted phrase
                        const words = rawWords.map(w => this.normalizeWord(w));
                        terms.push(new ParsedTerm(phrase, '', true, false, words));
                    }
                }
            } else if (match[2] !== undefined) { // Individual word
                let word = match[2];

                // Skip OR operators
                if (word.toLowerCase() === 'or') {
                    continue;
                }

                const isNegated = word.startsWith('-');
                if (isNegated) {
                    word = word.substring(1);
                }

                // Skip stop words and empty words
                if (!this.stopWords.has(word.toLowerCase()) && word) {
                    const normalized = this.normalizeWord(word);
                    if (normalized) {
                        terms.push(new ParsedTerm(word, normalized, false, isNegated));
                    }
                }
            }
        }

        return terms;
    }

    /**
     * Normalizes a word (lowercase, remove punctuation, basic stemming)
     * @param {string} word - Word to normalize
     * @returns {string} Normalized word
     */
    static normalizeWord(word) {

        // Convert to lowercase and normalize Unicode
        word = word.toLowerCase().normalize('NFD');

        // Remove punctuation and accents
        word = word.replace(/[^\w]/g, '');

        if (!word) return '';

        // Check exceptions first
        if (this.stemExceptions.has(word)) {
            return word;
        }
        // Apply basic stemming
        for (const [suffix, replacement] of this.stemRules) {
            if (word.endsWith(suffix) && word.length > suffix.length + 1) {
                return word.substring(0, word.length - suffix.length) + replacement;
            }
        }

        return word;
    }

    /**
     * Finds matches for individual words in text
     * @param {string} plainText - Text to search in
     * @param {ParsedTerm} term - Term to search for
     * @param {SearchMatch[]} matches - Array to add matches to
     */
    static findWordMatches(plainText, term, matches) {
        // Create pattern for word boundary matching with case insensitive flag
        const escapedTerm = term.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`\\b${escapedTerm}\\w*\\b`, 'gi');
        let match;

        while ((match = pattern.exec(plainText)) !== null) {
            // Verify the match by normalizing and comparing
            const matchedWord = this.normalizeWord(match[0]);
            if (matchedWord.startsWith(term.normalizedText)) { 
            // if (matchedWord.startsWith(term.normalizedText) || 
            //     term.normalizedText.startsWith(matchedWord)) {
                matches.push(new SearchMatch(
                    term.text,
                    match[0],
                    match.index,
                    match[0].length
                ));
            }
        }
    }

    /**
     * Finds matches for phrase searches in text
     * @param {string} plainText - Text to search in
     * @param {ParsedTerm} term - Phrase term to search for
     * @param {SearchMatch[]} matches - Array to add matches to
     */
    static findPhraseMatches(plainText, term, matches) {
        if (!term.words || term.words.length === 0) {
            return;
        }

        // Split text into words with their positions
        const wordPattern = /\b\w+\b/g;
        const words = [];
        let wordMatch;

        while ((wordMatch = wordPattern.exec(plainText)) !== null) {
            words.push({
                text: wordMatch[0],
                normalized: this.normalizeWord(wordMatch[0]),
                offset: wordMatch.index,
                length: wordMatch[0].length
            });
        }

        // Look for sequences of words that match the phrase
        for (let i = 0; i <= words.length - term.words.length; i++) {
            let phraseMatch = true;
            let wordIndex = 0;
            let startOffset = -1;
            let endOffset = -1;
            let j = i;

            // Check if consecutive words match the phrase
            while (j < words.length && wordIndex < term.words.length) {
                const currentWord = words[j];

                // Skip stop words in the text but not in our search terms.
                // However, this application considers stop words to be part of quoted phrases
                // if (this.stopWords.has(currentWord.text.toLowerCase())) {
                //     j++;
                //     continue;
                // }

                if (currentWord.normalized.startsWith(term.words[wordIndex])) {
                // if (currentWord.normalized.startsWith(term.words[wordIndex]) || 
                //     term.words[wordIndex].startsWith(currentWord.normalized)) {
                    if (startOffset === -1) {
                        startOffset = currentWord.offset;
                    }
                    endOffset = currentWord.offset + currentWord.length;
                    wordIndex++;
                } else {
                    phraseMatch = false;
                    break;
                }
                j++;
            }

            if (phraseMatch && wordIndex === term.words.length && startOffset >= 0) {
                matches.push(new SearchMatch(
                    term.text,
                    plainText.substring(startOffset, endOffset),
                    startOffset,
                    endOffset - startOffset
                ));
            }
        }
    }
}